import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Banknote, Check, X } from 'lucide-react';
import { ApiClientError, EmptyState, Money, Spinner, enumLabel, relativeTime } from '@riskpay/ui-components';
import { api } from '../lib/client';

/* ==========================================================================
 *  Payout control
 *
 *  Every payout on the platform, and the switch that closes one out by hand.
 *
 *  Marking one successful stamps the bank UTR and nothing else moves — the
 *  money left the available balance when the payout was queued. Marking one
 *  failed reverses that debit in full, fee included, because a transfer the
 *  bank refused is money the merchant still has.
 *
 *  A UTR is mandatory on success for the same reason it is mandatory on a
 *  settlement: a payout marked paid with no bank reference cannot be matched
 *  against a statement, and an unmatchable payout is an argument waiting to
 *  happen.
 * ========================================================================== */

interface Payout {
  id: string;
  status: 'QUEUED' | 'PROCESSING' | 'SUCCESS' | 'FAILED' | 'REVERSED' | 'ON_HOLD';
  purpose: string;
  rail: string;
  amount: { amount: string; currency: string; formatted: string };
  fee: { amount: string; currency: string; formatted: string };
  total_debited: { amount: string; currency: string; formatted: string };
  beneficiary: { name: string; last4: string | null; ifsc: string | null; vpa: string | null } | null;
  utr: string | null;
  failure_reason: string | null;
  created_at: string;
  processed_at: string | null;
  merchant: { ref: string; name: string } | null;
}

const FILTERS = [
  { value: 'OPEN', label: 'Awaiting the bank' },
  { value: 'SUCCESS', label: 'Paid' },
  { value: 'REVERSED', label: 'Reversed' },
  { value: 'ALL', label: 'All' },
] as const;

const OPEN = ['QUEUED', 'PROCESSING'];

export function Payouts() {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<string>('OPEN');
  const [openId, setOpenId] = useState<string | null>(null);
  const [utr, setUtr] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const payouts = useQuery({
    queryKey: ['admin-payouts', status],
    queryFn: () => api.get<{ data: Payout[] }>(`/payouts?status=${status}`),
    refetchInterval: 30_000,
  });

  const settle = useMutation({
    mutationFn: (v: { id: string; outcome: 'SUCCESS' | 'FAILED' }) =>
      api.post(`/payouts/${v.id}/reconcile`, {
        status: v.outcome,
        ...(v.outcome === 'SUCCESS' ? { utr: utr.trim() } : { failure_reason: reason.trim() || undefined }),
      }),
    onSuccess: () => {
      setOpenId(null);
      setUtr('');
      setReason('');
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['admin-payouts'] });
    },
    onError: (err) => setError(err instanceof ApiClientError ? err.message : 'Could not close that payout.'),
  });

  const rows = payouts.data?.data ?? [];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold tracking-tight">Payouts</h2>
        <p className="mt-1.5 max-w-2xl text-sm leading-relaxed text-content-muted">
          Money leaving merchant balances, across every account. Confirm one against the bank statement to stamp its
          UTR, or reverse it — a reversal returns the amount and its fee to the merchant in full.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2 border-b border-line pb-3">
        <div className="flex rounded-md border border-line bg-surface-inset p-0.5" role="group" aria-label="Filter">
          {FILTERS.map((f) => (
            <button
              key={f.value}
              type="button"
              onClick={() => setStatus(f.value)}
              aria-pressed={status === f.value}
              className={`rounded px-3 py-1.5 text-2xs font-medium transition-colors ${
                status === f.value ? 'bg-surface-overlay text-content' : 'text-content-subtle hover:text-content-muted'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <p role="alert" className="flex items-start gap-2 rounded-md border border-danger-ring bg-danger-soft px-3.5 py-3 text-sm text-danger-text">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          {error}
        </p>
      )}

      {payouts.isLoading ? (
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      ) : payouts.isError ? (
        <p role="alert" className="rounded-md border border-danger-ring bg-danger-soft px-3.5 py-3 text-sm text-danger-text">
          Could not load payouts.{' '}
          {payouts.error instanceof ApiClientError ? payouts.error.message : 'The request failed.'}
        </p>
      ) : rows.length === 0 ? (
        <div className="panel">
          <EmptyState title="Nothing matches this filter" description="Payouts appear the moment a merchant queues one." />
        </div>
      ) : (
        <ul className="space-y-3">
          {rows.map((payout) => {
            const open = openId === payout.id;
            const actionable = OPEN.includes(payout.status);
            return (
              <li key={payout.id} className="rounded-lg border border-line bg-surface-raised">
                <div className="flex flex-wrap items-start gap-4 p-4 sm:p-5">
                  <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-line bg-surface-inset">
                    <Banknote className="h-4 w-4 text-content-subtle" aria-hidden="true" />
                  </span>

                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2.5">
                      <Money amount={payout.amount.amount} currency={payout.amount.currency} className="text-md font-semibold" />
                      <span
                        className={`badge ${
                          payout.status === 'SUCCESS'
                            ? 'badge-success'
                            : payout.status === 'FAILED' || payout.status === 'REVERSED'
                              ? 'badge-danger'
                              : 'badge-info'
                        }`}
                      >
                        {enumLabel(payout.status)}
                      </span>
                      <span className="badge badge-neutral">{payout.rail}</span>
                    </div>

                    <p className="mt-2 text-xs text-content-subtle">
                      <span className="text-content">{payout.merchant?.name ?? '—'}</span>
                      {payout.merchant && <span className="numeral"> · {payout.merchant.ref}</span>}
                      {' · '}
                      <span className="numeral">{payout.id}</span>
                      {' · '}
                      {enumLabel(payout.purpose)}
                      {' · '}
                      {relativeTime(payout.created_at)}
                    </p>

                    <p className="mt-1.5 text-xs text-content-subtle">
                      To <span className="text-content">{payout.beneficiary?.name ?? '—'}</span>
                      {payout.beneficiary?.vpa && <span className="numeral"> · {payout.beneficiary.vpa}</span>}
                      {payout.beneficiary?.last4 && (
                        <span className="numeral">
                          {' '}
                          · ••••{payout.beneficiary.last4} · {payout.beneficiary.ifsc}
                        </span>
                      )}
                      {' · debited '}
                      <span className="numeral text-content-muted">{payout.total_debited.formatted}</span>
                      {' (fee '}
                      <span className="numeral">{payout.fee.formatted}</span>)
                    </p>

                    {payout.utr && (
                      <p className="numeral mt-1.5 text-2xs text-content-faint">UTR {payout.utr}</p>
                    )}
                    {payout.failure_reason && (
                      <p className="mt-2 rounded border border-danger-ring bg-danger-soft px-3 py-2 text-xs text-danger-text">
                        {payout.failure_reason}
                      </p>
                    )}
                  </div>

                  {actionable && !open && (
                    <button
                      type="button"
                      onClick={() => { setOpenId(payout.id); setUtr(''); setReason(''); setError(null); }}
                      className="btn btn-secondary btn-sm shrink-0"
                    >
                      Close out
                    </button>
                  )}
                </div>

                {open && (
                  <div className="border-t border-line bg-surface-inset/40 p-4 sm:p-5">
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div>
                        <label htmlFor={`utr-${payout.id}`} className="label">
                          Bank UTR
                        </label>
                        <input
                          id={`utr-${payout.id}`}
                          value={utr}
                          onChange={(e) => setUtr(e.target.value)}
                          placeholder="Required to mark it paid"
                          className="input numeral"
                        />
                      </div>
                      <div>
                        <label htmlFor={`why-${payout.id}`} className="label">
                          Reversal reason
                        </label>
                        <input
                          id={`why-${payout.id}`}
                          value={reason}
                          onChange={(e) => setReason(e.target.value)}
                          placeholder="Shown to the merchant"
                          className="input"
                        />
                      </div>
                    </div>

                    <div className="mt-4 flex flex-wrap items-center justify-end gap-2 border-t border-line pt-4">
                      <button type="button" onClick={() => setOpenId(null)} className="btn btn-ghost btn-sm">
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={() => settle.mutate({ id: payout.id, outcome: 'FAILED' })}
                        disabled={settle.isPending}
                        className="btn btn-sm border-danger-ring bg-danger-soft text-danger-text hover:bg-danger-soft"
                      >
                        <X className="h-3.5 w-3.5" aria-hidden="true" />
                        Reverse · refund the merchant
                      </button>
                      <button
                        type="button"
                        onClick={() => settle.mutate({ id: payout.id, outcome: 'SUCCESS' })}
                        disabled={settle.isPending || utr.trim().length < 6}
                        className="btn btn-primary btn-sm"
                      >
                        {settle.isPending ? <Spinner /> : <Check className="h-3.5 w-3.5" aria-hidden="true" />}
                        Mark paid
                      </button>
                    </div>
                    {utr.trim().length > 0 && utr.trim().length < 6 && (
                      <p className="mt-2 text-right text-2xs text-content-faint">A UTR is at least six characters.</p>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
